import { memo, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import NextImage from 'next/image';
import { useRouter } from 'next/router';

import { Box, FormHelp, Label, LoadingMask, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import { photoUtils } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyLogoV2WebApiHandler_delete,
  useRentalManagerUserPropertyLogoWebApiHandler_upload,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../../hooks/usePropertyEditMutation';
import { getPhotoUrls } from '../../../../../utils/propertiesPageUtils';
import { getIsInputFormDisabled } from '../../../utils';

import { useAuth } from '../../../../AuthProvider';
import UploadDropzone from '../../../../UploadDropzone';

import type { FileRejection } from 'react-dropzone';

export function formatMaxFileSizeMsg(origMsg: string) {
  const msgArr = origMsg.split(':');
  const value = Number(msgArr[1].trim()) / 1024 / 1024 || 0;
  return `File must be under ${value}MB`;
}

const LogoUpload = () => {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const logo = listingDetails?.logo;
  const isFeed = listingDetails?.isFeed || false;
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress || false;
  const isSSOBUpdateRestricted = listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted || false;

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isRunningInProd = window.location.hostname.includes('zillow.com');

  const { mutateAsync: deleteLogo, isPending: isDeleting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyLogoV2WebApiHandler_delete,
    successToastMessage: 'Logo deleted',
    meta: { context: { api: 'RentalManagerUserPropertyLogoV2WebApiHandler_delete' } },
  });

  const { mutateAsync: uploadLogo, isPending: isUploading } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyLogoWebApiHandler_upload,
    successToastMessage: 'Logo uploaded',
    meta: { context: { api: 'RentalManagerUserPropertyLogoWebApiHandler_upload' } },
  });

  const [imgMinHeight, setImgMinHeight] = useState(150);

  const onDropRejected = (errorData?: Array<FileRejection | { errors?: Array<{ message: string; code?: string }> }>) =>
    enqueueToast(
      <Toast tone="critical">
        {errorData?.[0]?.errors?.[0]?.message ?? 'Photo couldn’t be uploaded. Wait and try again.'}
      </Toast>,
    );

  const onDropAccepted = (acceptedFiles: Array<File>) => {
    // Filter HEIC files
    const filteredFile = photoUtils.filterForHEIC([acceptedFiles[0]], onDropRejected)?.[0];

    if (!filteredFile) {
      return;
    }

    const fileTmpURL = URL.createObjectURL(filteredFile);
    const img = new Image();

    img.src = fileTmpURL;

    img.onload = () => {
      const { width, height } = img;

      setImgMinHeight((height / width) * 250);

      URL.revokeObjectURL(fileTmpURL);
    };

    uploadLogo({
      getParams: { listingAlias },
      postData: { sourceFile: filteredFile },
    }).catch((err) => {
      let msg = err?.message || 'Failed to upload logo. Refresh the page and try again.';

      if (err?.code === 'ECONNABORTED') {
        msg = 'Upload couldn’t be completed. Reload the page and try again.';
      } else if (msg.toLowerCase().includes('file size exceeds maximum')) {
        msg = formatMaxFileSizeMsg(msg);
      }

      enqueueToast(<Toast tone="critical">{msg}</Toast>);
    });
  };

  const removeLogoHandler = () => {
    deleteLogo({ postData: { listingAlias } }).catch((err) => {
      enqueueToast(<Toast tone="critical">{err?.message || 'Logo couldn’t be deleted. Try again.'}</Toast>);
    });
  };

  const isSelfServeOnboardingInProgressAndSSOBUpdateRestricted =
    isSelfServeOnboardingInProgress && isSSOBUpdateRestricted;
  const isMutating = isUploading || isDeleting;
  const isUploadDisabled = isMutating || isSelfServeOnboardingInProgressAndSSOBUpdateRestricted;

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    accept: ['image/*'],
    onDropAccepted,
    onDropRejected,
    disabled: isUploadDisabled,
  });

  const rootProps = useMemo(
    () => getRootProps({ isFocused, isDragAccept, isDragReject, acceptedFiles }),
    [getRootProps, isFocused, isDragAccept, isDragReject, acceptedFiles],
  );
  const inputProps = useMemo(() => getInputProps(), [getInputProps]);

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  return (
    <Box data-testid="LogoUpload" css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
      <Label size="lg" disabled={isInputFormDisabled}>
        Logo
      </Label>

      <LoadingMask.Root loading={isFetchingListingDetails || isMutating}>
        <LoadingMask.Content css={{ width: '100%' }}>
          {logo?.url ? (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
              <Box css={{ position: 'relative', display: 'inline-block', width: '250px' }}>
                <Box
                  css={{
                    borderRadius: '100',
                    overflow: 'hidden',
                  }}
                  style={{ minHeight: `${imgMinHeight}px` }}
                >
                  {logo?.errorDto?.errorCode ? (
                    <Box
                      css={{
                        backgroundSize: logo?.errorDto?.replacementPhotoId ? 'cover' : '200px',
                        backgroundColor: 'bg.accent.aqua.impact',
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'no-repeat',
                        width: '100%',
                      }}
                      style={{
                        minHeight: `${imgMinHeight}px`,
                        backgroundImage: logo?.errorDto?.replacementPhotoId
                          ? `url(${getPhotoUrls(isRunningInProd, logo?.errorDto?.replacementPhotoId).thumb})`
                          : '',
                      }}
                    />
                  ) : (
                    <>
                      {logo?.url && (
                        <NextImage
                          alt={`${listingDetails?.name} logo`}
                          src={logo.url}
                          height={imgMinHeight}
                          width={250}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {logo?.errorDto?.errorMessage && <FormHelp error={true}>{logo?.errorDto?.errorMessage}</FormHelp>}

              {!isFeed && (
                <Box css={{ display: 'flex' }}>
                  <TextButton
                    tone="neutral"
                    icon={<IconDeleteOutline />}
                    onClick={removeLogoHandler}
                    disabled={isInputFormDisabled}
                  >
                    Delete
                  </TextButton>
                </Box>
              )}
            </Box>
          ) : (
            <>
              {!isFeed && (
                <UploadDropzone
                  isDisabled={isUploadDisabled}
                  isEmpty={true}
                  rootProps={rootProps}
                  inputProps={inputProps}
                  displayText="Drag logo image here"
                />
              )}
            </>
          )}
        </LoadingMask.Content>
        <LoadingMask.Spinner />
      </LoadingMask.Root>
    </Box>
  );
};

export default memo(LogoUpload);
